import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  clearStoredAdminToken,
  getAdminMe,
  getStoredAdminToken,
} from '../services/adminAuthApi';
import {
  getProductBySlug,
  resolveMediaUrl,
  resolveProductCardImage,
} from '../services/productsApi';
import { parseDescriptionBullets } from '../utils/parseProductDescription';

const ProductDetail = ({ product, onBack, onAddToQuoteCart }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [detailProduct, setDetailProduct] = useState(product);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [activeOptionKey, setActiveOptionKey] = useState(null);
  const [attributePreviewImage, setAttributePreviewImage] = useState(null);
  const [selectedFinishId, setSelectedFinishId] = useState('');
  const [quoteOptionByAttribute, setQuoteOptionByAttribute] = useState({});
  const [quoteFinishSelections, setQuoteFinishSelections] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [finishPreview, setFinishPreview] = useState(null);
  const [finishReference, setFinishReference] = useState('');
  const [quoteNotice, setQuoteNotice] = useState(null);

  useEffect(() => {
    setQuoteOptionByAttribute({});
    setQuoteFinishSelections([]);
    setSelectedFinishId('');
    setFinishPreview(null);
    setFinishReference('');
  }, [product?.id]);

  useEffect(() => {
    const html = document.documentElement;
    const previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.scrollTop = 0;
    document.body.scrollTop = 0;
    html.style.scrollBehavior = previousBehavior;
  }, [product?.id, product?.slug]);

  useEffect(() => {
    let mounted = true;

    const loadProductDetail = async () => {
      if (!product?.slug) {
        setDetailProduct(product);
        return;
      }

      try {
        setIsLoadingDetail(true);
        const data = await getProductBySlug(product.slug);
        if (!mounted) return;
        // Preferir siempre lo que viene del detalle (aunque venga vacío).
        // Si no hay cover/variant y reusamos la cover del listado, se bloquea
        // la galería de imágenes de atributos.
        const fromApi = Array.isArray(data?.images)
          ? data.images
              .map((image) => ({
                path: resolveMediaUrl(image.image_path),
                type: image.image_type || 'other',
              }))
              .filter((image) => image.path)
          : null;
        const fromList = Array.isArray(product.images)
          ? product.images
              .map((p) => resolveMediaUrl(p))
              .filter(Boolean)
              .map((path) => ({ path, type: 'other' }))
          : [];
        const mergedImages = fromApi !== null ? fromApi : fromList;

        setDetailProduct({
          ...data,
          images: mergedImages,
        });
        setActiveIndex(0);
        setActiveOptionKey(null);
        setAttributePreviewImage(null);
        setSelectedFinishId('');
        setFinishPreview(null);
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      } catch (error) {
        if (!mounted) return;
        setDetailProduct(product);
      } finally {
        if (mounted) {
          setIsLoadingDetail(false);
          requestAnimationFrame(() => {
            window.scrollTo(0, 0);
          });
        }
      }
    };

    loadProductDetail();

    return () => {
      mounted = false;
    };
  }, [product]);

  useEffect(() => {
    let mounted = true;

    const checkAdminSession = async () => {
      const token = getStoredAdminToken();
      if (!token) {
        if (mounted) setIsAdmin(false);
        return;
      }
      try {
        await getAdminMe(token);
        if (mounted) setIsAdmin(true);
      } catch {
        clearStoredAdminToken();
        if (mounted) setIsAdmin(false);
      }
    };

    checkAdminSession();

    const handleAuthChanged = () => {
      checkAdminSession();
    };
    window.addEventListener('verissa-admin-auth-changed', handleAuthChanged);

    return () => {
      mounted = false;
      window.removeEventListener(
        'verissa-admin-auth-changed',
        handleAuthChanged
      );
    };
  }, []);

  const currentProduct = useMemo(() => detailProduct || product, [detailProduct, product]);

  const descriptionBullets = useMemo(
    () => parseDescriptionBullets(currentProduct?.description),
    [currentProduct?.description]
  );

  const productAttributes = useMemo(() => {
    const attrs = currentProduct?.attributes;
    if (!Array.isArray(attrs)) return [];
    return attrs
      .map((attr) => ({
        ...attr,
        options: Array.isArray(attr.options) ? attr.options : [],
      }))
      .filter((attr) => attr.options.length > 0);
  }, [currentProduct?.attributes]);

  const finishAttributes = useMemo(
    () => productAttributes.filter((attr) => attr.attribute_type === 'image'),
    [productAttributes]
  );

  const otherAttributes = useMemo(
    () => productAttributes.filter((attr) => attr.attribute_type !== 'image'),
    [productAttributes]
  );

  const selectedFinishAttribute = useMemo(
    () =>
      finishAttributes.find(
        (attr) => String(attr.id) === String(selectedFinishId)
      ) || null,
    [finishAttributes, selectedFinishId]
  );

  const finishSearchQuery = finishReference.trim();
  const finishSearchResults = useMemo(() => {
    const query = finishSearchQuery
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    if (!query) return [];
    const results = [];
    finishAttributes.forEach((attribute) => {
      attribute.options.forEach((option) => {
        const haystack = `${attribute.label} ${option.label}`
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        if (haystack.includes(query)) {
          results.push({ attribute, option });
        }
      });
    });
    return results;
  }, [finishAttributes, finishSearchQuery]);

  // Galería: principal/variante → si no hay, imágenes de atributos (no acabados) → ambientada al final
  const displayImages = useMemo(() => {
    const rawImages = Array.isArray(currentProduct?.images)
      ? currentProduct.images
      : [];

    const normalize = (img) => {
      if (!img) return null;
      if (typeof img === 'string') {
        return { path: resolveMediaUrl(img), type: 'other' };
      }
      return {
        path: resolveMediaUrl(img.path || img.image_path || ''),
        type: img.type || img.image_type || 'other',
      };
    };

    const productImages = rawImages.map(normalize).filter((img) => img?.path);
    const coverImages = productImages.filter((img) => img.type === 'cover');
    const variantImages = productImages.filter((img) => img.type === 'variant');
    const otherImages = productImages.filter(
      (img) => !['cover', 'variant', 'ambient'].includes(img.type)
    );
    const ambientImages = productImages.filter((img) => img.type === 'ambient');

    const attributeImages = [];
    otherAttributes.forEach((attribute) => {
      attribute.options.forEach((option) => {
        if (!option?.image_path) return;
        const path = resolveMediaUrl(option.image_path);
        if (path) attributeImages.push({ path, type: 'attribute' });
      });
    });

    const hasPrincipalOrVariant =
      coverImages.length > 0 || variantImages.length > 0;

    // Sin principal/variante: usar atributos (aunque haya ambientada u "other" del listado)
    const primaryGallery = hasPrincipalOrVariant
      ? [...coverImages, ...variantImages, ...otherImages]
      : attributeImages.length > 0
        ? attributeImages
        : otherImages;

    const seen = new Set();
    const unique = (items) =>
      items.filter((item) => {
        const key = resolveMediaUrl(item.path);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    return [...unique(primaryGallery), ...unique(ambientImages)];
  }, [currentProduct?.images, otherAttributes]);

  const activeGalleryImage = useMemo(() => {
    if (attributePreviewImage) {
      return {
        path: resolveMediaUrl(attributePreviewImage),
        type: 'attribute',
      };
    }
    return (
      displayImages[activeIndex] ||
      displayImages[0] || {
        path: '/assets/Images/inicio.jpg',
        type: 'other',
      }
    );
  }, [attributePreviewImage, displayImages, activeIndex]);

  const mainImageSrc = activeGalleryImage.path;
  const isAmbientMainImage = activeGalleryImage.type === 'ambient';

  useEffect(() => {
    if (!finishPreview) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setFinishPreview(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [finishPreview]);

  useEffect(() => {
    if (!quoteNotice) return undefined;
    const timer = window.setTimeout(() => setQuoteNotice(null), 4500);
    return () => window.clearTimeout(timer);
  }, [quoteNotice]);

  if (!product) return null;

  // Solo atributos del producto (no acabados/telas) pueden cambiar la imagen principal
  const showOptionImage = (attributeId, option) => {
    if (!option?.image_path) return;
    setAttributePreviewImage(option.image_path);
    setActiveOptionKey(`${attributeId}-${option.id}`);
  };

  const isAttributeOptionSelected = (attributeId, optionId) =>
    String(quoteOptionByAttribute[attributeId] || '') === String(optionId);

  const toggleAttributeOption = (attribute, option) => {
    setQuoteOptionByAttribute((prev) => {
      const current = prev[attribute.id];
      if (String(current || '') === String(option.id)) {
        const next = { ...prev };
        delete next[attribute.id];
        return next;
      }
      return { ...prev, [attribute.id]: option.id };
    });
  };

  const isFinishSelected = (attributeId, optionId) =>
    quoteFinishSelections.some(
      (item) =>
        String(item.attributeId) === String(attributeId) &&
        String(item.optionId) === String(optionId)
    );

  const toggleFinishSelection = (attribute, option) => {
    setQuoteFinishSelections((prev) => {
      const exists = prev.some(
        (item) =>
          String(item.attributeId) === String(attribute.id) &&
          String(item.optionId) === String(option.id)
      );
      if (exists) {
        return prev.filter(
          (item) =>
            !(
              String(item.attributeId) === String(attribute.id) &&
              String(item.optionId) === String(option.id)
            )
        );
      }
      return [
        ...prev,
        {
          attributeId: attribute.id,
          attributeLabel: attribute.label,
          optionId: option.id,
          optionLabel: option.label,
        },
      ];
    });
  };

  const resolveQuoteCartImage = () => {
    const selectedOptions = [];
    otherAttributes.forEach((attribute) => {
      const selectedId = quoteOptionByAttribute[attribute.id];
      if (!selectedId) return;
      const selected = attribute.options.find(
        (option) => String(option.id) === String(selectedId)
      );
      if (selected) selectedOptions.push(selected);
    });
    if (selectedOptions.length === 0) return '';

    const selectedWithImage = selectedOptions.find((option) => option.image_path);
    if (selectedWithImage?.image_path) return selectedWithImage.image_path;

    const cardSource = product?.cover_image || product?.images?.[0];
    return (
      resolveProductCardImage(cardSource, product || currentProduct) ||
      cardSource ||
      ''
    );
  };

  const buildBaseQuoteAttributes = () => {
    const chosen = [];
    otherAttributes.forEach((attribute) => {
      const optionId = quoteOptionByAttribute[attribute.id];
      if (!optionId) return;
      const option = attribute.options.find(
        (item) => String(item.id) === String(optionId)
      );
      if (!option) return;
      chosen.push({
        attributeLabel: attribute.label,
        optionLabel: option.label,
      });
    });
    return chosen;
  };

  const buildQuoteAttributes = () => {
    const chosen = buildBaseQuoteAttributes();
    quoteFinishSelections.forEach((item) => {
      chosen.push({
        attributeLabel: item.attributeLabel,
        optionLabel: item.optionLabel,
      });
    });
    if (finishSearchQuery && finishSearchResults.length === 0) {
      chosen.push({
        attributeLabel: 'Color de referencia',
        optionLabel: finishSearchQuery,
      });
    }
    return chosen;
  };

  const openFinishPreview = (attribute, option) => {
    if (!option?.image_path) return;
    setFinishPreview({
      src: resolveMediaUrl(option.image_path),
      label: option.label || 'Acabado',
      attributeId: attribute.id,
      attributeLabel: attribute.label,
      optionId: option.id,
      optionLabel: option.label,
    });
  };

  const selectPreviewedFinish = () => {
    if (!finishPreview?.optionId) return;
    toggleFinishSelection(
      {
        id: finishPreview.attributeId,
        label: finishPreview.attributeLabel,
      },
      {
        id: finishPreview.optionId,
        label: finishPreview.optionLabel,
      }
    );
    setFinishPreview(null);
  };

  const removeFinishSelection = (item) => {
    setQuoteFinishSelections((prev) =>
      prev.filter(
        (entry) =>
          !(
            String(entry.attributeId) === String(item.attributeId) &&
            String(entry.optionId) === String(item.optionId)
          )
      )
    );
  };

  const handleThumbClick = (index) => {
    setActiveIndex(index);
    setAttributePreviewImage(null);
    setActiveOptionKey(null);
  };

  const addCurrentProductToCart = (selectedAttributes) => {
    const qty = Number(quantity) || 0;
    if (!onAddToQuoteCart || qty <= 0) return;
    const quoteImage = resolveQuoteCartImage();
    onAddToQuoteCart(
      {
        ...currentProduct,
        selectedAttributes,
        ...(quoteImage
          ? { images: [quoteImage], quoteImageLocked: true }
          : {}),
      },
      qty
    );
    setQuoteNotice({
      id: Date.now(),
      name: currentProduct?.name || 'seleccionado',
    });
  };

  const handleAddClick = () => {
    addCurrentProductToCart(buildQuoteAttributes());
  };

  const renderFinishOption = (attribute, option, showFamily = false) => {
    const isSelected = isFinishSelected(attribute.id, option.id);
    const openThisFinish = () => {
      if (option.image_path) {
        openFinishPreview(attribute, option);
        return;
      }
      toggleFinishSelection(attribute, option);
    };
    return (
      <div
        key={`${attribute.id}-${option.id}`}
        className={`product-detail-finish-option ${
          option.image_path ? 'has-image' : ''
        } ${isSelected ? 'is-quote-selected' : ''}`}
      >
        <button
          type="button"
          className="product-detail-finish-preview"
          onClick={openThisFinish}
          aria-label={
            option.image_path
              ? `Ver acabado ${option.label} ampliado`
              : `${isSelected ? 'Quitar' : 'Elegir'} acabado ${option.label}`
          }
        >
          {option.image_path ? (
            <img
              src={resolveMediaUrl(option.image_path)}
              alt={option.label}
              className="product-detail-finish-thumb"
            />
          ) : (
            <span className="product-detail-finish-placeholder" />
          )}
        </button>
        <button
          type="button"
          className="product-detail-finish-pick"
          aria-pressed={isSelected}
          onClick={openThisFinish}
        >
          <span>{option.label}</span>
          {showFamily && (
            <span className="product-detail-finish-family">
              {attribute.label}
            </span>
          )}
        </button>
      </div>
    );
  };

  const renderFinishChooser = (selectId) => (
    <>
      <div className="product-detail-finish-controls">
        <select
          id={selectId}
          className="product-detail-finish-select"
          value={selectedFinishId}
          onChange={(event) => {
            setSelectedFinishId(event.target.value);
          }}
        >
          <option value="">Selecciona un acabado</option>
          {finishAttributes.map((attribute) => (
            <option key={attribute.id} value={attribute.id}>
              {attribute.grade
                ? `${attribute.label} (Grado ${attribute.grade})`
                : attribute.label}
            </option>
          ))}
        </select>
        <input
          id="product-finish-search"
          className="product-detail-finish-search"
          type="search"
          value={finishReference}
          onChange={(event) => setFinishReference(event.target.value)}
          placeholder="Ej. Rojo"
          aria-label="Buscar acabado o color de referencia"
        />
      </div>

      {finishSearchQuery && finishSearchResults.length === 0 && (
        <p className="product-detail-finish-search-note">
          No hay un acabado con “{finishSearchQuery}”. Esa palabra queda como
          referencia.
        </p>
      )}

      {finishSearchQuery && finishSearchResults.length > 0 && (
        <div className="product-detail-attribute-options is-finish">
          {finishSearchResults.map(({ attribute, option }) =>
            renderFinishOption(attribute, option, true)
          )}
        </div>
      )}

      {!finishSearchQuery && selectedFinishAttribute && (
        <div className="product-detail-attribute-options is-finish">
          {selectedFinishAttribute.options.map((option) =>
            renderFinishOption(selectedFinishAttribute, option)
          )}
        </div>
      )}
    </>
  );

  const handleMainImageZoomMove = (event) => {
    const frame = event.currentTarget;
    const rect = frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    frame.style.setProperty('--zoom-x', `${x}%`);
    frame.style.setProperty('--zoom-y', `${y}%`);
  };

  const handleMainImageZoomLeave = (event) => {
    const frame = event.currentTarget;
    frame.style.setProperty('--zoom-x', '50%');
    frame.style.setProperty('--zoom-y', '50%');
  };

  const handleAdminEditClick = () => {
    const productId = currentProduct?.id;
    if (!productId) return;
    window.location.hash = `admin?edit=${productId}`;
  };

  return (
    <section className="product-detail-page">
      <div className="product-detail-top-bar">
        <button className="product-back-btn" type="button" onClick={onBack}>
          ← Volver a productos
        </button>
        {isAdmin && currentProduct?.id && (
          <button
            type="button"
            className="product-admin-edit-btn"
            onClick={handleAdminEditClick}
          >
            Editar producto
          </button>
        )}
      </div>
      <div className="product-detail-container">
        <div className="product-detail-images">
          <div
            className={`product-detail-main-image${
              isAmbientMainImage ? ' is-ambient' : ''
            }`}
            onMouseMove={handleMainImageZoomMove}
            onMouseLeave={handleMainImageZoomLeave}
          >
            <img
              src={mainImageSrc}
              alt={currentProduct.name}
            />
          </div>
          {displayImages.length > 1 && (
            <div className="product-detail-thumbs">
              {displayImages.map((img, index) => {
                const thumbSrc = resolveMediaUrl(img.path);
                const isActive = attributePreviewImage
                  ? resolveMediaUrl(attributePreviewImage) === thumbSrc
                  : index === activeIndex;
                const isAmbientThumb = img.type === 'ambient';
                return (
                  <motion.div
                    key={`${img.path}-${index}`}
                    className={`product-thumb${isActive ? ' active' : ''}${
                      isAmbientThumb ? ' is-ambient' : ''
                    }`}
                    whileHover={{ scale: 1.03 }}
                    onClick={() => handleThumbClick(index)}
                  >
                    <img
                      src={thumbSrc}
                      alt={`${currentProduct.name} vista ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
        <div className="product-detail-info">
          <div className="product-detail-info-content">
          <span className="product-category">{currentProduct.category}</span>
          <h1 className="product-detail-title">{currentProduct.name}</h1>
          {currentProduct.description &&
            (descriptionBullets ? (
              <>
                <p className="product-detail-description product-detail-features-label">
                  {descriptionBullets.title
                    ? `${descriptionBullets.title}:`
                    : 'Características:'}
                </p>
                <ul className="product-detail-bullets">
                  {descriptionBullets.items.map((item, index) => (
                    <li key={`${index}-${item.slice(0, 24)}`}>{item}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="product-detail-description product-detail-description-plain">
                {currentProduct.description}
              </p>
            ))}
          {currentProduct.dimensions && (
            <p className="product-detail-description">
              <strong>Dimensiones:</strong> {currentProduct.dimensions}
            </p>
          )}
          {currentProduct.materials && (
            <p className="product-detail-description">
              <strong>Materiales:</strong> {currentProduct.materials}
            </p>
          )}
          {isLoadingDetail && (
            <p className="product-detail-description">Cargando detalle...</p>
          )}

          {(finishAttributes.length > 0 || otherAttributes.length > 0) && (
            <div className="product-detail-attributes">
              {otherAttributes.map((attribute) => (
                <div
                  className={`product-detail-attribute ${
                    attribute.attribute_type === 'color' ? 'is-color-type' : ''
                  }`}
                  key={attribute.id}
                >
                  <h2 className="product-detail-attribute-title">
                    {attribute.label}
                  </h2>
                  <div
                    className={`product-detail-attribute-options ${
                      attribute.attribute_type === 'color'
                        ? 'is-color'
                        : 'is-text'
                    }`}
                  >
                    {attribute.options.map((option) => {
                      const hasImage = Boolean(option.image_path);
                      const optionKey = `${attribute.id}-${option.id}`;
                      const isActive = activeOptionKey === optionKey;
                      const isSelected = isAttributeOptionSelected(
                        attribute.id,
                        option.id
                      );
                      const isColor = attribute.attribute_type === 'color';

                      return (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={isSelected}
                          className={`${
                            isColor
                              ? 'product-detail-color-option'
                              : 'product-detail-text-option'
                          } is-quote-choice ${hasImage ? 'has-image' : ''} ${
                            isActive ? 'active' : ''
                          } ${isSelected ? 'is-quote-selected' : ''}`}
                          onClick={() => {
                            if (hasImage) {
                              showOptionImage(attribute.id, option);
                            }
                            toggleAttributeOption(attribute, option);
                          }}
                        >
                          {isColor && (
                            <span
                              className="product-detail-color-swatch"
                              style={{
                                backgroundColor:
                                  option.hex_color || '#cccccc',
                              }}
                            />
                          )}
                          {hasImage && !isColor && (
                            <img
                              src={resolveMediaUrl(option.image_path)}
                              alt=""
                              className="product-detail-text-option-thumb"
                            />
                          )}
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {finishAttributes.length > 0 && (
                <div className="product-detail-attribute product-detail-finish-dropdown">
                  <label
                    className="product-detail-attribute-title"
                    htmlFor="product-finish-select"
                  >
                    Acabados
                  </label>
                  {renderFinishChooser('product-finish-select')}
                </div>
              )}
            </div>
          )}
          </div>

          <div className="product-quote-actions">
            {(quoteFinishSelections.length > 0 ||
              (finishSearchQuery && finishSearchResults.length === 0)) && (
              <ul className="product-detail-selected-finishes">
                {quoteFinishSelections.map((item) => (
                  <li key={`${item.attributeId}-${item.optionId}`}>
                    <span>
                      {item.attributeLabel}: {item.optionLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFinishSelection(item)}
                    >
                      Quitar
                    </button>
                  </li>
                ))}
                {finishSearchQuery && finishSearchResults.length === 0 && (
                  <li>
                    <span>Color de referencia: {finishSearchQuery}</span>
                    <button
                      type="button"
                      onClick={() => setFinishReference('')}
                    >
                      Quitar
                    </button>
                  </li>
                )}
              </ul>
            )}
            <div className="product-qty-group">
              <label htmlFor="quote-qty">Cantidad</label>
              <input
                id="quote-qty"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn-quote-cart"
              onClick={handleAddClick}
            >
              Agregar a Carrito de Cotización
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {quoteNotice && (
          <motion.div
            key={quoteNotice.id}
            className="quote-added-toast"
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            <span className="quote-added-toast-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path
                  d="M20 6.5 9.5 17 4 11.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p>
              El producto <strong>{quoteNotice.name}</strong> fue agregado al
              carrito de cotización
            </p>
            <button
              type="button"
              className="quote-added-toast-close"
              onClick={() => setQuoteNotice(null)}
              aria-label="Cerrar aviso"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {finishPreview && (
        <div
          className="finish-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Acabado ${finishPreview.label}`}
          onClick={() => setFinishPreview(null)}
        >
          <div
            className="finish-preview-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="finish-preview-toolbar">
              <button
                type="button"
                className="finish-preview-select"
                onClick={selectPreviewedFinish}
              >
                {isFinishSelected(
                  finishPreview.attributeId,
                  finishPreview.optionId
                )
                  ? 'Quitar'
                  : 'Seleccionar'}
              </button>
              <button
                type="button"
                className="finish-preview-close"
                onClick={() => setFinishPreview(null)}
                aria-label="Cerrar vista de acabado"
              >
                ×
              </button>
            </div>
            <div
              className="finish-preview-zoom"
              onMouseMove={handleMainImageZoomMove}
              onMouseLeave={handleMainImageZoomLeave}
            >
              <img
                src={finishPreview.src}
                alt={finishPreview.label}
                className="finish-preview-image"
              />
            </div>
            <p className="finish-preview-label">{finishPreview.label}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductDetail;


